import React, { FC, ReactNode, useCallback } from 'react';
import { debounce } from 'lodash';
import { Range, RangeProps, WithTooltipProps, createSliderWithTooltip } from 'rc-slider';
import classnames from 'classnames';
import { useSearchParams } from 'react-router-dom';

import {
  generateMarks,
  getRangeValueFromParams,
  getTooltipValue,
  SliderElement
} from 'buyside/util/filterSliderUtil/filterSliderUtil';

export const RangeSliderWithTooltip = createSliderWithTooltip(Range);

const dotStyle = {
  borderColor: 'transparent',
  backgroundColor: 'transparent',
  outline: 0
};

const handleStyle = {
  backgroundColor: '#4a90e2',
  border: '4px solid #1f6ac2',
  outline: 0,
  marginTop: '-5px',
  marginLeft: 0,
  width: '14px',
  height: '14px',
  cursor: 'pointer',
  borderRadius: '50%'
};

const trackStyle = {
  backgroundColor: '#4a90e2'
};

import styles from './filterSliderRangeStyles.scss';

const FilterSliderRange: FC<{
  debounceDuration?: number;
  handleTipFormatter?: (value: string) => ReactNode;
  mapping: SliderElement[];
} & RangeProps &
  WithTooltipProps & {
    paramName: string;
    increments: (string | number)[];
    text?: string;
    className?: string;
  }> = ({
  mapping,
  debounceDuration = 1500,
  handleTipFormatter,
  paramName,
  increments,
  text,
  className
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const currentParamValue = searchParams.get(paramName);

  const setParamValue = useCallback(
    (paramValue: string) => {
      searchParams.set('page', '1');
      if (paramValue) {
        searchParams.set(paramName, paramValue);
      } else {
        searchParams.delete(paramName);
      }
      searchParams.delete('recommended');
      searchParams.delete('fpid');
      setSearchParams(searchParams);
    },
    [paramName, searchParams, setSearchParams]
  );

  const parsedCurrentValue = getRangeValueFromParams(currentParamValue || '', increments);
  const [sliderParsedValue, setParsedValue] = React.useState(parsedCurrentValue);
  const maxSliderValue = increments.length - 1;

  const delayedQuery = debounce(
    (min: number, max: number): void =>
      setParamValue(
        min !== 0 || max !== maxSliderValue ? `${mapping[min].value}_${mapping[max].value}` : ''
      ),
    debounceDuration
  );

  React.useEffect(() => {
    if (!parsedCurrentValue) setParsedValue([]);
  }, [parsedCurrentValue]);

  return (
    <div
      id={paramName + '-filter-slider-range-container'}
      className={classnames(styles.container, className)}
    >
      {text && <label>{text}</label>}

      <RangeSliderWithTooltip
        onChange={(value): void => {
          setParsedValue(value);
        }}
        onAfterChange={value => delayedQuery(value[0], value[1])}
        value={sliderParsedValue}
        dotStyle={dotStyle}
        handleStyle={handleStyle}
        trackStyle={[trackStyle]}
        marks={generateMarks(mapping)}
        tipFormatter={(index: number): React.ReactNode => {
          return handleTipFormatter
            ? handleTipFormatter(getTooltipValue(mapping, index))
            : getTooltipValue(mapping, index);
        }}
        max={maxSliderValue}
        defaultValue={parsedCurrentValue}
      />
    </div>
  );
};

export default FilterSliderRange;
